import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';

import { LocalTypeahead } from '@/components/common/local-typeahead';
import { DateInput } from '@/components/form/date-input';
import { GlCodeTypeahead } from '@/components/search/gl-code-typeahead';
import { Modal } from '@/components/ui/modal';
import { MoneyTextInput } from '@/components/ui/money-text-input';
import {
	useAccountingPermissions, useAccounts, useCreateCharge, useCreateCredit, useCreatePayment, useCreateRefund,
	useUnwaiveCharge, useUpdateCharge, useUpdateCredit, useUpdatePayment, useUpdateRefund,
} from '@/hooks/use-accounting';
import { useAssociations } from '@/hooks/use-data';
import { useDirtyConfirm } from '@/hooks/use-dirty-confirm';
import { GlCodeAccountType, PaymentMethod } from '@/types/models';
import type { Account, Association, GlCode } from '@/types/models';
import { accountDisplayName } from '@/utils/account-display';
import { Alert } from '@/utils/alert';
import { todayBusinessMs } from '@/utils/date-utils';
import { formatMoneyCents } from '@/utils/format-money';

/**
 * Only money-movement actions live in the modal. Creating a brand new account
 * is a richer workflow and lives at /accounting/new, so 'account' isn't part
 * of ActionKind anymore — the FAB handles routing to that page separately.
 */
export type ActionKind = 'account' | 'charge' | 'payment' | 'credit' | 'refund' | 'late_fee' | null;

type TxnKind = Exclude<NonNullable<ActionKind>, 'account'>;

interface EditingTxn {
	id: string; amountCents: number; memo?: string; occurredOn: number; category?: string;
	chargeStatus?: 'paid' | 'partial' | 'unpaid' | 'waived';
	method?: string;
	/** Credit / refund: the GL the row currently posts to, from the ledger row. */
	glCode?: GlCodeRef;
}

/** The slice of a GL code the picker needs — what a ledger row carries for its GL. */
type GlCodeRef = Pick<GlCode, 'id' | 'code' | 'name'>;

/**
 * Which GL codes each kind's picker offers — mirrors the server's
 * CREDIT_GL_TYPES / REFUND_GL_TYPES. A credit debits any posting code but AR or a
 * bank (an income code to forgive, Bad Debt to write off — money received is a
 * payment); a refund pays money out, so it credits a bank code.
 */
const GL_PICKER: Partial<Record<TxnKind, { label: string; placeholder: string; filter: (g: GlCode) => boolean }>> = {
	credit: {
		label: 'GL (debited by this credit — e.g. Bad Debt for a write-off)',
		placeholder: 'Search GL codes…',
		filter: (g) => g.accountType !== GlCodeAccountType.AccountsReceivable
			&& g.accountType !== GlCodeAccountType.Bank
			&& g.accountType !== GlCodeAccountType.NonPosting,
	},
	refund: {
		label: 'Paid from (bank GL)',
		placeholder: 'Search bank codes…',
		filter: (g) => g.accountType === GlCodeAccountType.Bank,
	},
};

interface Props {
	kind: ActionKind;
	onClose: () => void;
	/** Pre-select a specific account — used from the account detail page. */
	initialAccountId?: string;
	/** Pre-filter the account picker to a specific association — used from
	 *  the association dashboard so users don't have to re-pick it. */
	initialAssociationId?: string;
	/** When present, the form edits an existing row instead of creating a new one. */
	editing?: EditingTxn;
	/** When provided in edit mode, shows a Delete button (admin/accounting only). */
	onDelete?: () => void;
}

// Default category labels. Stored as the raw label string — the schema
// allows free-form text, so users can also type-in new ones via the
// typeahead's "Use: <text>" option.
// id === label so the user-visible category is what gets persisted;
// e.g. picking "Water" stores "Water" in charges.category, not the legacy
// 'special' enum. Legacy rows still render via the humanizer in formatCategory().
const CHARGE_CATEGORY_OPTIONS: Array<{ id: string; label: string }> = [
	{ id: 'Dues', label: 'Dues' },
	{ id: 'Late Fee', label: 'Late Fee' },
	{ id: 'Water', label: 'Water' },
	{ id: 'Legal Fees', label: 'Legal Fees' },
	{ id: 'Administration', label: 'Administration' },
	{ id: 'Clubhouse', label: 'Clubhouse' },
	{ id: 'Special Assessment', label: 'Special Assessment' },
	{ id: 'Returned Payment Fee', label: 'Returned Payment Fee' },
	{ id: 'Other', label: 'Other' },
];

const PAYMENT_METHOD_OPTIONS: Array<{ id: PaymentMethod; label: string }> = [
	{ id: PaymentMethod.Check, label: 'Check' },
	{ id: PaymentMethod.Cash, label: 'Cash' },
	{ id: PaymentMethod.ACH, label: 'ACH' },
	{ id: PaymentMethod.Card, label: 'Card' },
	{ id: PaymentMethod.Other, label: 'Other' },
];

const DATE_LABEL: Record<TxnKind, string> = {
	charge: 'Posted on',
	late_fee: 'Posted on',
	payment: 'Received on',
	credit: 'Issued on',
	refund: 'Issued on',
};

// YYYY-MM-DD <-> business-date (midnight UTC ms) converters. Both directions
// MUST use UTC fields: `parseIsoDate` writes with `Date.UTC`, so reading back
// with local getters west of Greenwich returned the PREVIOUS day — the field
// showed 08/13 for a charge stored as Aug 14, and saving re-posted it as Aug 13.
// The date walked backward one day on every edit.
const toIsoDate = (ms: number): string => {
	const d = new Date(ms);
	const y = d.getUTCFullYear();
	const m = String(d.getUTCMonth() + 1).padStart(2, '0');
	const day = String(d.getUTCDate()).padStart(2, '0');
	return `${y}-${m}-${day}`;
};
const parseIsoDate = (s: string): number | null => {
	const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	if (!m) return null;
	const [, y, mo, d] = m;
	const ms = Date.UTC(Number(y), Number(mo) - 1, Number(d));
	return isFinite(ms) ? ms : null;
};

const parseAmountToCents = (s: string): number | null => {
	const n = Number(s.replace(/[^0-9.-]/g, ''));
	if (!isFinite(n) || n <= 0) return null;
	return Math.round(n * 100);
};

const LABELS: Record<TxnKind, { title: string; submit: string; editTitle: string }> = {
	charge: { title: 'Post charge', submit: 'Post charge', editTitle: 'Edit charge' },
	payment: { title: 'Record payment', submit: 'Record payment', editTitle: 'Edit payment' },
	credit: { title: 'Issue credit', submit: 'Issue credit', editTitle: 'Edit credit' },
	refund: { title: 'Issue refund', submit: 'Issue refund', editTitle: 'Edit refund' },
	late_fee: { title: 'Post late fee', submit: 'Post late fee', editTitle: 'Edit late fee' },
};

export const NewActionModal: React.FC<Props> = ({ kind, onClose, initialAccountId, initialAssociationId, editing, onDelete }) => {
	const dirty = useDirtyConfirm();
	if (!kind || kind === 'account') return null;
	const txn = kind as TxnKind;
	const isEdit = !!editing;
	const guardedClose = () => dirty.confirmClose(onClose);
	return (
		<Modal visible onDismiss={guardedClose} contentContainerStyle={styles.modal}>
			<Header title={isEdit ? LABELS[txn].editTitle : LABELS[txn].title} onClose={guardedClose} />
			<TxnForm
				kind={txn}
				onClose={onClose}
				markDirty={dirty.markDirty}
				markClean={dirty.markClean}
				initialAccountId={initialAccountId}
				initialAssociationId={initialAssociationId}
				submitLabel={isEdit ? 'Save changes' : LABELS[txn].submit}
				editing={editing}
				onDelete={onDelete}
			/>
		</Modal>
	);
};

const Header: React.FC<{ title: string; onClose: () => void }> = ({ title, onClose }) => (
	<View style={styles.header}>
		<Text style={styles.title}>{title}</Text>
		<Pressable onPress={onClose} hitSlop={10}>
			<Text style={styles.close}>✕</Text>
		</Pressable>
	</View>
);

// --- Txn (charge / payment / credit / refund / late_fee) ------------------

const TxnForm: React.FC<{
	kind: Exclude<NonNullable<ActionKind>, 'account'>;
	onClose: () => void;
	markDirty: () => void;
	markClean: () => void;
	initialAccountId?: string;
	initialAssociationId?: string;
	submitLabel: string;
	editing?: EditingTxn;
	onDelete?: () => void;
}> = ({ kind, onClose, markDirty, markClean, initialAccountId, initialAssociationId, submitLabel, editing, onDelete }) => {
	const isEdit = !!editing;
	// The accounting write set: Admin role, Accounting role, or `accounting_admin` — the same
	// holders the server lets delete and back-date money movements.
	const { canModify } = useAccountingPermissions();
	const canDelete = canModify;
	// Only accounting writers may change dates. Everyone else sees
	// today's date locked in (on create) or the existing date read-only
	// (on edit) — preserves the audit trail without making the field
	// invisible.
	const canEditDate = canModify;
	const { data: associations } = useAssociations({ limit: 200 });
	const [assocId, setAssocId] = useState<string | undefined>(initialAssociationId);
	const { data: accountsResp } = useAccounts(assocId ? { associationId: assocId, limit: 500 } : { limit: 500 });
	const [accountId, setAccountId] = useState<string | undefined>(initialAccountId);
	const [amount, setAmount] = useState(editing ? (editing.amountCents / 100).toFixed(2) : '');
	const [memo, setMemo] = useState(editing?.memo ?? '');
	const [dateStr, setDateStr] = useState(toIsoDate(editing?.occurredOn ?? todayBusinessMs()));
	const [category, setCategory] = useState<string>(
		editing?.category ?? (kind === 'late_fee' ? 'Late Fee' : 'Dues'),
	);
	const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PaymentMethod.Check);
	const [reference, setReference] = useState<string>('');
	const glPicker = GL_PICKER[kind];
	// The selected GL object, seeded from the row being edited. Synchronously, not
	// resolved by a query after mount: GlCodeTypeahead clears a value whose text is
	// blank, so a GL that arrived a render late would be wiped on edit.
	const [glCode, setGlCode] = useState<GlCodeRef | null>(editing?.glCode ?? null);
	const [error, setError] = useState<string | null>(null);
	const [postedToast, setPostedToast] = useState<string | null>(null);

	const createCharge = useCreateCharge();
	const updateCharge = useUpdateCharge();
	const createPayment = useCreatePayment();
	const updatePayment = useUpdatePayment();
	const createCredit = useCreateCredit();
	const updateCredit = useUpdateCredit();
	const createRefund = useCreateRefund();
	const updateRefund = useUpdateRefund();
	const unwaiveCharge = useUnwaiveCharge();
	const busy = createCharge.isPending || updateCharge.isPending
		|| createPayment.isPending || updatePayment.isPending
		|| createCredit.isPending || updateCredit.isPending
		|| createRefund.isPending || updateRefund.isPending
		|| unwaiveCharge.isPending;

	useEffect(() => { if (initialAccountId) setAccountId(initialAccountId); }, [initialAccountId]);

	const accounts = useMemo<Account[]>(() => (accountsResp?.data ?? []) as Account[], [accountsResp]);
	const lockedAssociation = useMemo<Association | null>(
		() => initialAssociationId
			? ((associations?.data ?? []) as Association[]).find((a) => a.id === initialAssociationId) ?? null
			: null,
		[associations, initialAssociationId],
	);

	const save = async (continueAfter = false) => {
		if (!isEdit && !accountId) { setError('Pick an account.'); return; }
		const cents = parseAmountToCents(amount);
		if (!cents) { setError('Enter a positive dollar amount.'); return; }
		const occurredOn = parseIsoDate(dateStr);
		if (occurredOn == null) { setError('Enter a valid date (YYYY-MM-DD).'); return; }
		// Without a GL the server parks the entry in Unassigned Income / Expense.
		if (glPicker && !glCode) { setError('Pick a GL code.'); return; }
		const glCodeId = glCode?.id;
		// On edit, only send a GL the user changed — an imported row may carry a code
		// of a type the picker doesn't offer, and re-sending it would be rejected.
		const glPatch = glCodeId && glCodeId !== editing?.glCode?.id ? { glCodeId } : {};
		setError(null);
		setPostedToast(null);
		try {
			if (isEdit && editing) {
				// Build per-kind patch: amount/memo are universal; the date
				// field name differs per resource.
				const baseBody = { amountCents: cents, memo };
				if (kind === 'charge' || kind === 'late_fee') {
					const patch = canEditDate
						? { ...baseBody, postedOn: occurredOn, category }
						: { ...baseBody, category };
					await updateCharge.mutateAsync({ id: editing.id, body: patch });
				} else if (kind === 'payment') {
					const patch: Record<string, unknown> = canEditDate ? { ...baseBody, receivedOn: occurredOn } : { ...baseBody };
					// Reassign to a different account (moves the AR leg + re-applies FIFO).
					if (accountId && accountId !== initialAccountId) patch.accountId = accountId;
					await updatePayment.mutateAsync({ id: editing.id, body: patch });
				} else if (kind === 'credit') {
					const patch = canEditDate ? { ...baseBody, ...glPatch, issuedOn: occurredOn } : { ...baseBody, ...glPatch };
					await updateCredit.mutateAsync({ id: editing.id, body: patch });
				} else if (kind === 'refund') {
					const patch = canEditDate ? { ...baseBody, ...glPatch, issuedOn: occurredOn } : { ...baseBody, ...glPatch };
					await updateRefund.mutateAsync({ id: editing.id, body: patch });
				}
			} else {
				if (!accountId) { setError('Pick an account.'); return; }
				// On create, only admin/accounting can backdate; otherwise the
				// transaction records today's date.
				const effectiveDate = canEditDate ? occurredOn : todayBusinessMs();
				if (kind === 'charge' || kind === 'late_fee') {
					await createCharge.mutateAsync({
						accountId, postedOn: effectiveDate, dueOn: effectiveDate + 15 * 24 * 3600 * 1000,
						amountCents: cents,
						// A cleared category falls back to the server default rather than storing ''.
						category: category.trim() || undefined,
						memo,
					});
				} else if (kind === 'payment') {
					await createPayment.mutateAsync({
						accountId, receivedOn: effectiveDate, amountCents: cents,
						method: paymentMethod,
						reference: reference.trim() || undefined,
						memo,
					});
				} else if (kind === 'credit') {
					await createCredit.mutateAsync({ accountId, issuedOn: effectiveDate, amountCents: cents, memo, glCodeId });
				} else if (kind === 'refund') {
					await createRefund.mutateAsync({
						accountId, issuedOn: effectiveDate, amountCents: cents, method: PaymentMethod.Other, memo, glCodeId,
					});
				}
			}
			if (continueAfter && !isEdit) {
				// Keep account/association/date/method context; clear amount,
				// memo, and reference so the user can quickly post the next entry.
				setAmount('');
				setMemo('');
				setReference('');
				setPostedToast(`${LABELS[kind].submit.replace(/^Post |^Record |^Issue /, '')} posted — ready for the next entry.`);
			} else {
				markClean();
				onClose();
			}
		} catch (e) {
			setError((e as Error)?.message || 'Failed to save.');
		}
	};

	// Centralised "dirty + setState" wrapper so every field edit marks the
	// form dirty without sprinkling markDirty() calls into JSX.
	const onEdit = <T,>(fn: (v: T) => void) => (v: T) => { markDirty(); fn(v); };

	return (
		<ScrollView style={styles.body} contentContainerStyle={{ paddingBottom: 24 }}>
			{!initialAccountId && !isEdit && (
				<>
					{lockedAssociation ? (
						<>
							<Text style={styles.label}>Association</Text>
							<View style={styles.lockedField}>
								<Text style={styles.lockedFieldLabel}>{lockedAssociation.name}</Text>
							</View>
						</>
					) : (
						<View style={styles.typeaheadWrap}>
							<LocalTypeahead<Association>
								items={(associations?.data ?? []) as Association[]}
								toOption={(a) => ({ id: a.id, label: a.name })}
								value={assocId ?? null}
								onChange={(id) => { setAssocId(id ?? undefined); setAccountId(undefined); }}
								label="Association (filter)"
								placeholder="All associations"
							/>
						</View>
					)}
					<View style={styles.typeaheadWrap}>
						<LocalTypeahead<Account>
							items={accounts}
							toOption={(a) => ({
								id: a.id,
								label: a.accountNumber ?? a.id.slice(0, 8),
								description: formatMoneyCents(a.balanceCents),
							})}
							value={accountId ?? null}
							onChange={(id) => setAccountId(id ?? undefined)}
							label="Customer"
							placeholder="Search customers…"
						/>
					</View>
				</>
			)}
			{isEdit && kind === 'payment' && (
				<View style={styles.typeaheadWrap}>
					<LocalTypeahead<Account>
						items={accounts}
						toOption={(a) => ({ id: a.id, label: accountDisplayName(a), description: formatMoneyCents(a.balanceCents) })}
						value={accountId ?? null}
						onChange={(newId) => { setAccountId(newId ?? undefined); markDirty(); }}
						label="Associated account"
						placeholder="Search accounts…"
					/>
					{accountId && accountId !== initialAccountId && (
						<Text style={styles.reassignHint}>
							Moves this payment to the selected account — re-applies it FIFO there and recomputes both balances.
						</Text>
					)}
				</View>
			)}
			<Text style={styles.label}>Amount</Text>
			<MoneyTextInput style={styles.input} placeholder="0.00" placeholderTextColor="#bdbdbd" value={amount} onChangeText={onEdit(setAmount)} />
			<DateInput
				label={canEditDate ? DATE_LABEL[kind] : `${DATE_LABEL[kind]} (requires admin / accounting)`}
				disabled={!canEditDate}
				value={(() => { const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? new Date(`${dateStr}T05:00:00Z`) : null; })()}
				onChange={(d) => { onEdit(setDateStr)(d ? `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}` : ''); }}
			/>
			{(kind === 'charge' || kind === 'late_fee') && (
				<View style={styles.typeaheadWrap}>
					<LocalTypeahead<{ id: string; label: string }>
						items={CHARGE_CATEGORY_OPTIONS}
						toOption={(o) => ({ id: o.id, label: o.label })}
						value={category || null}
						onChange={(v) => setCategory(v ?? '')}
						label="Category"
						placeholder="Search or add a category…"
						allowCreate
					/>
				</View>
			)}
			{glPicker && (
				<View style={styles.typeaheadWrap}>
					<Text style={styles.label}>{glPicker.label}</Text>
					<GlCodeTypeahead
						value={glCode?.id ?? null}
						onSelect={(g) => { if (g?.id !== glCode?.id) markDirty(); setGlCode(g); }}
						filter={glPicker.filter}
						placeholder={glPicker.placeholder}
						seedText={editing?.glCode ? `${editing.glCode.code} — ${editing.glCode.name}` : undefined}
						testID={`${kind}-gl-code`}
					/>
				</View>
			)}
			{kind === 'payment' && (
				<View style={styles.typeaheadWrap}>
					<LocalTypeahead<{ id: PaymentMethod; label: string }>
						items={PAYMENT_METHOD_OPTIONS}
						toOption={(o) => ({ id: o.id, label: o.label })}
						value={paymentMethod}
						onChange={(v) => setPaymentMethod((v as PaymentMethod) ?? PaymentMethod.Check)}
						label="Payment method"
						placeholder="Select method…"
					/>
				</View>
			)}
			{kind === 'payment' && (
				<>
					<Text style={styles.label}>
						{paymentMethod === PaymentMethod.Check ? 'Check #' : 'Reference (optional)'}
					</Text>
					<TextInput
						style={styles.input}
						placeholder={paymentMethod === PaymentMethod.Check ? '1001' : 'Reference number'}
						placeholderTextColor="#bdbdbd"
						value={reference}
						onChangeText={onEdit(setReference)}
						autoCorrect={false}
					/>
				</>
			)}
			<Text style={styles.label}>Memo (optional)</Text>
			<TextInput style={styles.input} placeholder="Memo" placeholderTextColor="#bdbdbd" value={memo} onChangeText={onEdit(setMemo)} />

			{kind === 'payment' && !isEdit && (
				<Text style={styles.applyHint}>Applied to the oldest unpaid charges first.</Text>
			)}

			{error && <Text style={styles.error}>{error}</Text>}
			{postedToast && <Text style={styles.toast}>{postedToast}</Text>}
			<View style={styles.footerRow}>
				{isEdit && canDelete && onDelete && (
					<Pressable style={[styles.destructive, busy && styles.disabled]} disabled={busy} onPress={() => { onClose(); onDelete?.(); }}>
						<Text style={styles.destructiveLabel}>Delete</Text>
					</Pressable>
				)}
				{isEdit && editing && (kind === 'charge' || kind === 'late_fee') && editing.chargeStatus === 'waived' && (
					// Legacy waive: nothing creates one any more, but an existing waived row can still be restored.
					<Pressable
						style={[styles.refundBtn, busy && styles.disabled]}
						disabled={busy}
						onPress={() => {
							const chargeId = editing.id;
							onClose();
							Alert.alert(
								'Un-waive this charge?',
								'The charge will be restored to the account balance.',
								[
									{ text: 'Cancel', style: 'cancel' },
									{ text: 'Un-waive', onPress: () => unwaiveCharge.mutate(chargeId) },
								],
							);
						}}
					>
						<Ionicons name="refresh-outline" size={16} color="#1976d2" />
						<Text style={styles.refundBtnLabel}>Un-waive</Text>
					</Pressable>
				)}
				{isEdit && editing && kind === 'payment' && (
					<Pressable
						style={[styles.refundBtn, busy && styles.disabled]}
						disabled={busy}
						onPress={() => {
							const cents = parseAmountToCents(amount) ?? editing.amountCents;
							const acctId = initialAccountId ?? accountId;
							if (!acctId) { setError('Missing account context.'); return; }
							const validMethods = Object.values(PaymentMethod) as string[];
							const method: PaymentMethod = editing.method && validMethods.includes(editing.method)
								? (editing.method as PaymentMethod)
								: PaymentMethod.Other;
							const paymentId = editing.id;
							const memoText = memo.trim() || 'Refund of payment';
							onClose();
							Alert.alert(
								'Mark this payment refunded?',
								`A refund of ${formatMoneyCents(cents)} will be issued and linked to this payment.`,
								[
									{ text: 'Cancel', style: 'cancel' },
									{
										text: 'Refund', onPress: () => createRefund.mutate({
											accountId: acctId,
											paymentId,
											issuedOn: todayBusinessMs(),
											amountCents: cents,
											method,
											memo: memoText,
										}),
									},
								],
							);
						}}
					>
						<Ionicons name="arrow-undo-outline" size={16} color="#1976d2" />
						<Text style={styles.refundBtnLabel}>Refund</Text>
					</Pressable>
				)}
				{!isEdit && (
					<Pressable
						style={[styles.secondary, busy && styles.disabled]}
						disabled={busy}
						onPress={() => save(true)}
						accessibilityLabel="Save and start another entry"
					>
						<Text style={styles.secondaryLabel}>Save &amp; new</Text>
					</Pressable>
				)}
				<Pressable style={[styles.primary, { flex: 1 }, busy && styles.disabled]} disabled={busy} onPress={() => save(false)}>
					<Text style={styles.primaryLabel}>{busy ? 'Saving…' : submitLabel}</Text>
				</Pressable>
			</View>
		</ScrollView>
	);
};

const styles = StyleSheet.create({
	modal: { width: '92%', maxWidth: 520, maxHeight: '86%', borderRadius: 12, padding: 0, overflow: 'hidden' },
	header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#eee' },
	title: { fontSize: 18, fontWeight: '700' },
	close: { fontSize: 20, color: '#888', paddingHorizontal: 8 },
	body: { paddingHorizontal: 16, paddingTop: 12 },
	label: { fontSize: 12, fontWeight: '600', color: '#666', textTransform: 'uppercase', marginTop: 12, marginBottom: 6 },
	input: { borderWidth: 1, borderColor: '#ddd', borderRadius: 6, padding: 10, fontSize: 14, marginBottom: 8, backgroundColor: '#fff' },
	inputReadOnly: { backgroundColor: '#f5f5f5', color: '#666' },
	hint: { fontSize: 10, color: '#888', fontWeight: '400', textTransform: 'none' },
	inlineBox: { backgroundColor: '#fafafa', padding: 8, borderRadius: 6, marginBottom: 8 },
	pickerRow: { flexGrow: 0, marginBottom: 4 },
	chip: { paddingVertical: 6, paddingHorizontal: 12, borderRadius: 16, backgroundColor: '#f0f0f0', marginRight: 8, maxWidth: 260 },
	chipActive: { backgroundColor: '#007AFF' },
	chipLabel: { fontSize: 13, color: '#333' },
	chipLabelActive: { color: '#fff' },
	footerRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 16 },
	primary: { backgroundColor: '#007AFF', padding: 12, borderRadius: 6, alignItems: 'center' },
	destructive: { backgroundColor: '#fff', padding: 12, borderRadius: 6, alignItems: 'center', paddingHorizontal: 16, borderWidth: 1, borderColor: '#c62828' },
	destructiveLabel: { color: '#c62828', fontWeight: '500' },
	disabled: { opacity: 0.5 },
	primaryLabel: { color: '#fff', fontWeight: '600' },
	secondary: { backgroundColor: '#fff', padding: 12, borderRadius: 6, alignItems: 'center', paddingHorizontal: 14, borderWidth: 1, borderColor: '#1976d2' },
	secondaryLabel: { color: '#1976d2', fontWeight: '600' },
	error: { color: '#c62828', marginTop: 8, fontSize: 13 },
	toast: { color: '#2e7d32', marginTop: 8, fontSize: 13, fontWeight: '500' },
	empty: { color: '#888', fontStyle: 'italic' },
	applyHint: { fontSize: 12, color: '#607d8b', marginTop: 8 },
	lockedField: { padding: 10, borderRadius: 6, backgroundColor: '#f5f5f5', borderWidth: 1, borderColor: '#e0e0e0', marginBottom: 8 },
	typeaheadWrap: { position: 'relative' },
	reassignHint: { fontSize: 12, color: '#c62828', marginTop: 6 },
	lockedFieldLabel: { fontSize: 14, color: '#37474f', fontWeight: '500' },
	refundBtn: {
		flexDirection: 'row', alignItems: 'center', gap: 6,
		padding: 12, paddingHorizontal: 16,
		borderRadius: 6, borderWidth: 1, borderColor: '#1976d2', backgroundColor: '#fff',
	},
	refundBtnLabel: { color: '#1976d2', fontWeight: '500' },
});
